import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    HasProduct,
    HasProductContext,
    ProductContext,
    withProduct,
} from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import siteMessage from "@insite/client-framework/SiteMessage";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link from "@insite/mobius/Link";
import ADIColors from "@NA/Utils/ADIColors";
import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { css } from "styled-components";
import { pushDealerAuthDataToDataLayer } from "@NA/Overrides/Common/Utilities/CustomTracking";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
interface OwnProps {
    id: string;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const isAuthenticated = state.context.session!.isAuthenticated;
    const session = state.context.session;
    let customerNumber = "";
    const billToId = session?.billToId;

    if (billToId) {
        customerNumber = state.data.billTos.byId[billToId]?.customerNumber;
    }

    return {
        isAuthenticated,
        customerNumber,
        session,
    };
};

type Props = OwnProps & HasProduct & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ADIDealerStyles {
    title?: InjectableCss;
    mainContainer?: InjectableCss;
    message?: InjectableCss;
    messageLink?: InjectableCss;
    VerificationText?: TypographyPresentationProps;
}

export const adiDealerStyles: ADIDealerStyles = {
    title: {
        css: css`
            line-height: 30px;
            color: ${ADIColors.darkBlue1};
            font-size: 19px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            text-align: center;
            display: block;
        `,
    },
    mainContainer: {
        css: css`
            background: ${ADIColors.white4};
            padding: 9px 19px;
            border-radius: 5px;
        `,
    },
    message: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            font-weight: normal;
            color: ${ADIColors.darkBlack3};
            text-align: center;
            a {
                font-weight: bold;
                color: ${ADIColors.darkBlack3} !important;
                > span {
                    font-weight: bold;
                    color: ${ADIColors.darkBlack3} !important;
                }
            }
        `,
    },
    messageLink: {
        css: css`
            pointer-events: none;
        `,
    },
    VerificationText: {
        css: css`
                           
                                text-transform: none !important;
                                font-size: 14px !important;
                                font-weight: bold;
                                color: ${ADIColors.blue5} !important;                              
                            }
                        `,
    },
};

const styles = adiDealerStyles;

const ADIDealerCertification: React.FC<Props> = ({ product, isAuthenticated, id, customerNumber, session }) => {
    const productContext = React.useContext(ProductContext);
    const productInfo = productContext.productInfo;
    const pricing = productInfo?.pricing || product.pricing;

    const showAuthorizationRequired =
        pricing &&
        pricing.additionalResults &&
        pricing.additionalResults.isVisible &&
        pricing.additionalResults.isVisible === "N"
            ? "true"
            : "false";
    const vendorAuthRequiredLst = product?.properties?.vendorAuthRequiredList?.toLowerCase();
    const vendorAuthRequiredList =
        typeof vendorAuthRequiredLst === "string" ? vendorAuthRequiredLst.toLowerCase() : null;

    const brandName = product.brand?.name ?? "";
    const vendorAuthRequiredLink = product?.properties?.VendorAuthRequiredLink;
    const isPendingVerification = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    );
    const getCustomerCreditStatus = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore,
    );
    const clickHandler = () => {
        pushDealerAuthDataToDataLayer(brandName, vendorAuthRequiredLink, customerNumber, "Product Details", session);
    };
    const [accountPendingVerificationPDP, setaccountPendingVerificationPDP] = useState<string>("");

    useEffect(() => {
        setaccountPendingVerificationPDP(getCustomerCreditStatus?.getcustomerStatus?.creditStatus);
    }, [accountPendingVerificationPDP, getCustomerCreditStatus?.getcustomerStatus?.creditStatus]);
    return (
        <>
            {isAuthenticated &&
            showAuthorizationRequired === "true" &&
            !isPendingVerification &&
            vendorAuthRequiredList === "true" ? (
                <StyledWrapper {...styles.mainContainer}>
                    <StyledWrapper {...styles.title} as="span">
                        {" "}
                        {translate("Dealer Certification Required")}{" "}
                    </StyledWrapper>
                    <StyledWrapper {...styles.message}>
                        {siteMessage("Dealer_Vendor_Certification_Message")}{" "}
                        <Link href={product.properties?.vendorAuthRequiredLink} target="_blank" onClick={clickHandler}>
                            {translate("vendor's form.")}
                        </Link>{" "}
                        {translate("For more information, call ")}
                        <Link {...styles.messageLink} href="tel:1.877.228.6739">
                            1.877.228.6739.
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>
            ) : isAuthenticated &&
              showAuthorizationRequired === "true" &&
              !isPendingVerification &&
              vendorAuthRequiredList === "false" ? (
                <StyledWrapper {...styles.mainContainer}>
                    {accountPendingVerificationPDP !== "O" &&
                    accountPendingVerificationPDP !== "" &&
                    accountPendingVerificationPDP !== null ? (
                        <Typography {...styles.VerificationText} as="span">
                            {" "}
                            {translate("Account Pending Verification")}{" "}
                        </Typography>
                    ) : (
                        <>
                            <StyledWrapper {...styles.title} as="span">
                                {" "}
                                {translate("Dealer Certification Required")}{" "}
                            </StyledWrapper>
                            <StyledWrapper {...styles.message}>
                                {siteMessage("Dealer_Certification_Message")}
                            </StyledWrapper>{" "}
                        </>
                    )}
                </StyledWrapper>
            ) : (
                <StyledWrapper>
                    <Zone contentId={id} zoneName="NotDealerCertified"></Zone>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ADIDealerCertification)),
    definition: {
        group: "Basic",
        displayName: "ADI Dealer Certification Widget",
    },
};

export default widgetModule;
